import { Component, ElementRef, HostListener, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { ExchangeRateService, Currency } from '@/app/core/services/utils/exchange-rate.service';
import { CurrencyContextService } from '@/app/core/services/utils/currency-context.service';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { getisLoggedIn, getAuthMe } from '@/app/store/auth/auth.selector';
import { logout } from '@/app/store/auth/auth.actions';
import { AuthMe } from '@/app/core/models/auth';

@Component({
  selector: 'app-quick-actions-bar',
  standalone: true,
  imports: [RouterModule, CommonModule],
  templateUrl: './quick-actions-bar.component.html',
  styleUrls: ['./quick-actions-bar.component.scss']
})
export class QuickActionsBarComponent implements OnInit {
  openCurrency = false;
  openLanguage = false;
  openProfile = false;
  currencies: Currency[] = [];
  languages = ['ES', 'EN', 'FR'];
  selectedCurrencyCode: string | null = null;
  selectedLanguage = this.languages[0];
  isLoggedIn$!: Observable<boolean>;
  me$!: Observable<AuthMe | null>;
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private exchangeRate: ExchangeRateService,
    private currencyCtx: CurrencyContextService,
    private store: Store,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.isLoggedIn$ = this.store.select(getisLoggedIn);
    this.me$ = this.store.select(getAuthMe);

    // El catálogo de monedas (GET /currency) no se pide en el prerender: solo el navegador.
    if (!this.isBrowser) return;

    this.exchangeRate.getCurrencies(100, 1, 'id', 'asc', '').subscribe({
      next: (list) => {
        this.currencies = list;
        if (!this.selectedCurrencyCode && this.currencies.length) {
          // USD por defecto: es la moneda en la que Arcadia cotiza y liquida.
          // Antes esta linea elegia EUR y lo empujaba al contexto, pisando
          // cualquier default: el selector abria en euros para todo el mundo.
          const usd = this.currencies.find(c => c.code === 'USD');
          this.selectedCurrencyCode = (usd || this.currencies[0]).code;
          this.applyCurrencyContext(this.selectedCurrencyCode);
        }
      },
      error: () => {
        // Si ni siquiera se pudo traer el catalogo de monedas, queda USD.
        this.currencies = [
          { id: 0, name: 'US Dollar', code: 'USD', symbol: '$', eurConversionRate: 0 },
          { id: 1, name: 'EUR Euro', code: 'EUR', symbol: '€', eurConversionRate: 0 },
        ];
        this.selectedCurrencyCode = 'USD';
        this.applyCurrencyContext(this.selectedCurrencyCode);
      }
    });
  }

  toggleCurrency() { this.openCurrency = !this.openCurrency; this.openLanguage = false; this.openProfile = false; }
  toggleLanguage() { this.openLanguage = !this.openLanguage; this.openCurrency = false; this.openProfile = false; }
  toggleProfile()  { this.openProfile = !this.openProfile; this.openCurrency = false; this.openLanguage = false; }

  selectCurrency(code: string) {
    this.selectedCurrencyCode = code;
    this.openCurrency = false;
    this.applyCurrencyContext(code);
  }

  selectLanguage(l: string) {
    this.selectedLanguage = l;
    this.openLanguage = false;
    this.currencyCtx.setLocale(this.mapLanguageToLocale(l));
  }

  onLogout(evt?: Event) {
    if (evt) evt.preventDefault();
    this.openProfile = false;
    this.store.dispatch(logout());
  }

  private applyCurrencyContext(code: string) {
    const found = this.currencies.find(c => c.code === code);
    if (found) {
      this.currencyCtx.setCurrency({ id: found.id, code: found.code, symbol: found.symbol });
    }
  }

  private mapLanguageToLocale(lang: string): string {
    switch (lang) {
      case 'ES': return 'es-ES';
      case 'EN': return 'en-US';
      case 'FR': return 'fr-FR';
      default:   return 'es-ES';
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent) {
    if (!this.elRef.nativeElement.contains(ev.target as HTMLElement)) {
      this.openCurrency = false;
      this.openLanguage = false;
      this.openProfile = false;
    }
  }
}